import {
  ECreditApplicationStatus,
  EFinancingCompany,
  EPaymentPlan,
  EProgressionStatus,
} from '@if/core/types'
import { formatDateTime } from '@if/core/utils'
import * as yup from '@if/core/yup'
import { useUrlFiltersAndPagination } from '@if/mui'
import { Box, Button, Chip, Link, Stack, Typography } from '@mui/material'
import { DataGrid, GridColDef, GridFooter, GridFooterContainer } from '@mui/x-data-grid'
import {
  BROWSE_CREDIT_APPLICATIONS_FILTERS_ENABLED_KEY,
  BROWSE_CREDIT_APPLICATIONS_FILTERS_KEY,
} from '@src/contexts/preferences-context'
import { useUnreadEntries } from '@src/data/api/chat-message/chat-message-api'
import { useGetCreditDashboardEntries } from '@src/data/api/credit-dashboard-entry/credit-DashboardEntries-api'
import { usePreferences } from '@src/providers/PreferencesProvider'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useCallback, useEffect, useMemo, useState, type JSX } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import FullScreenContainer from '../../components/FullScreenContainer'
import {
  useApplicantTypeEnum,
  useCurrentLang,
  useDocumentTypes,
  useFeatureFlags,
  useMerchantTags,
  usePlanEnumForProgram,
} from '../../data/selectors/app-config-selectors'
import { CREDIT_DASHBOARD_FILTER_ALL, CreditDashboardEntry, CreditDashboardFilters } from '../../data/types/CreditDashboardEntry'
import { buildApiParams } from './buildApiParams'
import DataGridHeader from './DataGridHeader'
import InlineActions from './InlineActions'

const falsy = () => false

const EXCLUDE_FROM_PREFERENCES = ['searchTerm'] as const

// Schema for the filter values stored in the URL. Pagination and `financingCompanyId` are
// intentionally not part of the schema — pagination is owned by `useUrlFiltersAndPagination`,
// and `financingCompanyId` comes from the global PreferencesProvider context.
const filterSchema = yup.object({
  financingProgramIds: yup.array(yup.string().required()).default([]),
  territoryIds: yup.array(yup.string().required()).default([]),
  loanPurposeIds: yup.array(yup.string().required()).default([]),
  originSystemIds: yup.array(yup.string().required()).default([]),
  plan: yup.array(yup.string().required()).default([]),
  progressionStatus: yup.array(yup.string().required()).default([]),
  creditApplicationStatus: yup.string().default(ECreditApplicationStatus.Active),
  searchTerm: yup.string().default(''),
  merchantId: yup.string().default(''),
  dateRange: yup.string().default(CREDIT_DASHBOARD_FILTER_ALL),
  team: yup.string().default(CREDIT_DASHBOARD_FILTER_ALL),
  tag: yup.string().nullable().default(null),
  minAmountRequested: yup.string().default(''),
})

const BrowseCreditApplicationsPage = (): JSX.Element => {
  const { t } = useTranslation()
  document.title = t('breadcrumbs.home')

  const { selectedCompany } = usePreferences()
  const featureFlags = useFeatureFlags()

  const { filters, setFilters, reset, preferences, pagination, setPagination } =
    useUrlFiltersAndPagination(filterSchema, {
      storageKey: BROWSE_CREDIT_APPLICATIONS_FILTERS_KEY,
      preferencesEnabledKey: BROWSE_CREDIT_APPLICATIONS_FILTERS_ENABLED_KEY,
      excludeFromPreferences: EXCLUDE_FROM_PREFERENCES,
      defaultLimit: 50,
    })

  const [searchTerm, setSearchTerm] = useState(filters.searchTerm ?? '')
  const [stagedFilters, setStagedFilters] = useState({ ...filters })

  // `team` only applies to iFinance — clear it when company switches to Iceberg.
  useEffect(() => {
    if (selectedCompany === EFinancingCompany.Iceberg && filters.team && filters.team !== CREDIT_DASHBOARD_FILTER_ALL) {
      setFilters({ team: CREDIT_DASHBOARD_FILTER_ALL })
    }
  }, [selectedCompany, filters.team, setFilters])

  useEffect(() => {
    setSearchTerm(filters.searchTerm ?? '')
  }, [filters.searchTerm])

  useEffect(() => {
    setStagedFilters({ ...filters })
  }, [filters])

  const filterValues = useMemo<CreditDashboardFilters>(
    () => ({
      ...filters,
      financingCompanyId: selectedCompany ?? '',
      limit: pagination.limit,
      offset: pagination.offset,
    }),
    [filters, selectedCompany, pagination],
  )

  const stagedFilterValues = useMemo<CreditDashboardFilters>(
    () => ({
      ...stagedFilters,
      financingCompanyId: selectedCompany ?? '',
      limit: pagination.limit,
      offset: pagination.offset,
    }),
    [stagedFilters, selectedCompany, pagination],
  )

  const apiParams = useMemo(() => buildApiParams(filterValues), [filterValues])
  const [creditApplications, isLoading] = useGetCreditDashboardEntries(apiParams)

  const [unreadEntries] = useUnreadEntries(featureFlags.useInstantMessage)
  const unreadCreditAppIds = useMemo(
    () => new Set(unreadEntries.map((e) => e.creditApplicationId)),
    [unreadEntries],
  )

  const applicantTypeEnum = useApplicantTypeEnum()
  const documentTypes = useDocumentTypes()
  const lang = useCurrentLang()
  const tags = useMerchantTags()
  const planEnum = usePlanEnumForProgram()

  const getPlanTranslation = useCallback(
    (plan?: string | null) => {
      if (!plan) return ''
      const planEnumValue = planEnum[plan]
      if (!planEnumValue?.[lang]) {
        reportErrorToConsole(`Plan ${plan} not found in planEnum or translation for ${lang} not available`)
        return ''
      }
      return planEnumValue[lang]
    },
    [lang, planEnum],
  )

  const handleReset = useCallback(() => {
    setSearchTerm('')
    setStagedFilters(filterSchema.getDefault())
    reset()
  }, [reset])

  const handleApplyFilter = useCallback(
    (data: CreditDashboardFilters) => {
      // Pagination and `financingCompanyId` are not schema fields — strip them before
      // forwarding. `setFilters` auto-resets offset to 0 (page 1).
      const { limit: _l, offset: _o, financingCompanyId: _f, ...filterPatch } = data
      setFilters({ ...filterPatch, searchTerm })
      setStagedFilters(filterPatch)
    },
    [setFilters, searchTerm],
  )

  const handleOnPageChange = useCallback(
    (direction: number) => {
      const delta = direction === 0 ? -pagination.limit : pagination.limit
      setPagination({ offset: pagination.offset + delta, limit: pagination.limit })
    },
    [pagination, setPagination],
  )

  const handleSetSearchTerm = useCallback((value: string) => {
    setSearchTerm(value)
    setStagedFilters({ ...filterSchema.getDefault(), creditApplicationStatus: CREDIT_DASHBOARD_FILTER_ALL })
  }, [])

  const columns = useMemo<GridColDef<CreditDashboardEntry>[]>(
    () => [
      {
        field: 'financingProgramId',
        headerName: t('browseCreditApplications.dataGridColumnHeaders.type'),
        minWidth: 80,
        sortable: false,
        hide: true,
        renderCell: (params) =>
          t(`EFinancingProgram.${params.row.financingProgramId}`, { ns: 'enum', defaultValue: '' }),
      },
      {
        field: 'referenceNumber',
        headerName: t('browseCreditApplications.dataGridColumnHeaders.applicationNumber'),
        minWidth: 130,
        sortable: false,
        renderCell: (params) => {
          if (params.row.merchantName?.startsWith('createdFor')) return params.row.creditApplicationReferenceNumber
          return (
            <Link href={`/Applications/${params.row.financingProgramId}/${params.row.id}/view`}>
              {params.row.creditApplicationReferenceNumber}
            </Link>
          )
        },
      },
      {
        field: 'merchantName',
        headerName: t('browseCreditApplications.dataGridColumnHeaders.merchant'),
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          if (params.row.merchantName?.startsWith('createdFor'))
            return (
              <Trans
                i18nKey="browseCreditApplications.createdFor"
                values={{ financingCompany: `${params.row.merchantName.replace('createdFor', '')}` }}
              />
            )
          return params.row.merchantName ? (
            params.row.merchantName
          ) : (
            <Trans
              i18nKey="browseCreditApplications.dataGridHeader.missingMerchantName"
              values={{ merchantId: params.row.merchantId }}
            />
          )
        },
      },
      {
        field: 'clientName',
        headerName: t('browseCreditApplications.dataGridColumnHeaders.client'),
        flex: 1,
        sortable: false,
      },
      {
        field: 'progressionStatus',
        headerName: t('common.status'),
        minwidth: 80,
        sortable: false,
        renderCell: (params) => {
          if (!params.row.progressionStatus) return null
          let statusColor = '#d13126'
          const statuscode = t(`EProgressionStatus.${params.row.progressionStatus}`, { ns: 'enum' })
          const abbr = statuscode.substring(statuscode.length - 3, statuscode.length - 1)
          if (params.row.progressionStatus === EProgressionStatus.Approved) {
            statusColor = '#25c455'
          }
          if (
            params.row.progressionStatus === EProgressionStatus.ConditionalApproval ||
            params.row.progressionStatus === EProgressionStatus.CreditPending
          ) {
            statusColor = '#fcba03'
          }
          if (
            params.row.progressionStatus === EProgressionStatus.DocumentIncomplete ||
            params.row.progressionStatus === EProgressionStatus.DocumentReceived ||
            params.row.progressionStatus === EProgressionStatus.PaymentPending
          ) {
            statusColor = '#951dc4'
          }
          if (params.row.progressionStatus === EProgressionStatus.Booked) {
            statusColor = '#0ea8eb'
          }

          if (params.row.progressionStatus === EProgressionStatus.WithDrawn) {
            statusColor = '#ff5722'
          }
          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div
                style={{
                  backgroundColor: statusColor,
                  height: '12px',
                  width: '12px',
                  borderRadius: '12px',
                  marginRight: 5,
                }}
              />
              <div title={statuscode} style={{ marginLeft: 3 }}>
                {abbr}
              </div>
            </div>
          )
        },
      },
      {
        field: 'interestRate',
        headerName: t('browseCreditApplications.dataGridColumnHeaders.interestRate'),
        minWidth: 100,
        align: 'right',
        headerAlign: 'right',
        sortable: false,
        renderCell: (params) => {
          if (!params.row.interestRate) return null
          if (params.row.paymentPlanId !== EPaymentPlan.RegularDailyInterests) {
            return 'Promo'
          }
          return params.row.interestRate
        },
      },
      {
        field: 'plan',
        headerName: t('credit.plan'),
        minWidth: 100,
        sortable: false,
        renderCell: (params) => {
          return getPlanTranslation(params.row?.plan)
        },
      },
      {
        field: 'requestedLoanAmount',
        headerName: t('browseCreditApplications.dataGridColumnHeaders.requestedAmount'),
        minWidth: 120,
        align: 'right' as const,
        headerAlign: 'right' as const,
        sortable: false,
        renderCell: (params: { row: CreditDashboardEntry }) => {
          if (!params.row.requestedLoanAmount) return null
          return params.row.requestedLoanAmount
        },
      },
      {
        field: 'maxAmountFinanced',
        headerName: t('browseCreditApplications.dataGridColumnHeaders.grantedAmount'),
        minWidth: 120,
        align: 'right' as const,
        headerAlign: 'right' as const,
        sortable: false,
        renderCell: (params: { row: CreditDashboardEntry }) => {
          if (!params.row.maxAmountFinanced) return null
          return params.row.maxAmountFinanced
        },
      },
      {
        field: 'tags',
        headerAlign: 'left',
        headerName: t('browseCreditApplications.dataGridColumnHeaders.tags'),
        align: 'left',
        sortable: false,
        renderCell: (params) => {
          if (!params.row.tags) return null
          return (
            <Stack direction="row" gap={0.5}>
              {params.row.tags.map((tag) => (
                <Chip size="small" key={tag} label={tags.find((item) => item.id === tag)?.text} />
              ))}
            </Stack>
          )
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 260,
        sortable: false,
        renderCell: (params) => {
          return (
            <InlineActions
              id={params.row?.id}
              worksheetDecision={params.row?.worksheetDecision}
              hasDocumentsPendingReview={params.row?.hasDocumentsPendingReview}
              incomeConfirmed={params.row?.incomeConfirmed}
              missingRequiredDocuments={params.row?.missingRequiredDocuments}
              reservedByUserId={params.row?.reservedByUserId}
              reservedByUserName={params.row?.reservedByUserName}
              reservedUntil={params.row?.reservedUntil}
              missingDocumentsNames={documentTypes}
              applicantTypes={applicantTypeEnum}
              lang={lang}
              hasUnreadMessages={unreadCreditAppIds.has(params.row?.id)}
            />
          )
        },
      },
      {
        field: 'createdOn',
        headerName: t('browseCreditApplications.dataGridHeader.createdOn'),
        sortable: false,
        minWidth: 180,
        renderCell: (params) => {
          if (params.row.createdOn.toString() === '0001-01-01T00:00:00') return <Typography />
          return formatDateTime(params.row.createdOn)
        },
        valueFormatter: (value: Date) => formatDateTime(value),
      },
    ],
    [t, getPlanTranslation, tags, documentTypes, applicantTypeEnum, lang, unreadCreditAppIds],
  )

  const customFooter = () => {
    return (
      <GridFooterContainer>
        <GridFooter sx={{ '& .MuiTablePagination-actions': { display: 'none' } }} />
        <div style={{ display: 'flex' }}>
          <Button onClick={() => handleOnPageChange(0)} disabled={pagination.offset === 0}>
            {' '}
            {t('common.previous')}
          </Button>
          <Button onClick={() => handleOnPageChange(1)} disabled={creditApplications.length < pagination.limit}>
            {' '}
            {t('common.next')}
          </Button>
        </div>
      </GridFooterContainer>
    )
  }

  const paginationModel = useMemo(
    () => ({ pageSize: pagination.limit, page: Math.floor(pagination.offset / pagination.limit) }),
    [pagination.limit, pagination.offset],
  )

  return (
    <FullScreenContainer>
      <DataGridHeader
        filterValues={stagedFilterValues}
        resetFilter={handleReset}
        searchTerm={searchTerm}
        setSearchTerm={handleSetSearchTerm}
        applyFilter={handleApplyFilter}
        preferences={preferences}
      />
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DataGrid
          disableColumnFilter
          isRowSelectable={falsy}
          rows={creditApplications}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={({ pageSize }) =>
            setPagination({ limit: pageSize, offset: pagination.offset })
          }
          loading={isLoading}
          paginationMode="server"
          rowCount={creditApplications.length}
          sx={{ flex: 1 }}
          slots={{
            footer: customFooter,
          }}
          slotProps={{
            pagination: {
              labelRowsPerPage: t('common.customDisplayedRowsTextFooter.RowsPerPage'),
              labelDisplayedRows: () =>
                t('common.customDisplayedRowsTextFooter.displayRange', {
                  defaultValue: '',
                  from: pagination.offset + 1,
                  to: pagination.offset + pagination.limit,
                }),
            },
          }}
        />
      </Box>
    </FullScreenContainer>
  )
}

export default BrowseCreditApplicationsPage
